import * as XLSX from "xlsx";
import {
  formatSessionAmount,
  formatAllocationAmount,
} from "../constants/sessionConstants";
import { getSectorLabel } from "../constants/authConstants";

/**
 * Common function to format amount for Excel
 */
const formatAmountForExcel = (amount) => {
  if (!amount && amount !== 0) return 0;
  const numAmount = parseFloat(amount);
  return isNaN(numAmount) ? 0 : numAmount;
};

/**
 * Common function to create and download Excel file
 */
const downloadExcel = (workbook, filename) => {
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Export Session Budget (Department Wise)
 */
export const exportSessionBudgetToExcel = (
  data,
  filters,
  getDepartmentLabel,
) => {
  const { departments, sectorTotals } = data;

  // Prepare data rows
  const excelData = [];

  // Add title row
  excelData.push([
    `Session Budget - Department Wise (${
      filters.financialYear || "All Years"
    })`,
  ]);
  excelData.push([]); // Empty row

  // Add headers
  excelData.push([
    "S.No.",
    "Department",
    "Total Allocation (in lakh)",
    "State Plan (in lakh)",
    "Negotiated Loan (in lakh)",
  ]);

  let serialNumber = 1;

  // Group by sector
  const groupedBySector = {};
  departments.forEach((dept) => {
    if (!groupedBySector[dept.sector]) {
      groupedBySector[dept.sector] = [];
    }
    groupedBySector[dept.sector].push(dept);
  });

  // Add data rows sector by sector
  Object.keys(groupedBySector)
    .sort()
    .forEach((sectorKey) => {
      const sectorDepts = groupedBySector[sectorKey];

      // Add department rows
      sectorDepts.forEach((dept) => {
        excelData.push([
          serialNumber++,
          getDepartmentLabel(dept.department),
          formatAmountForExcel(dept.approvedTotal),
          formatAmountForExcel(dept.approvedStatePlan),
          formatAmountForExcel(dept.approvedNegotiatedLoan),
        ]);
      });

      // Add sector total
      const sectorTotal = sectorTotals[sectorKey];
      if (sectorTotal) {
        excelData.push([
          "",
          `Total - ${getSectorLabel(sectorKey)} (${
            sectorTotal.departmentCount
          } departments)`,
          formatAmountForExcel(sectorTotal.approvedTotal),
          formatAmountForExcel(sectorTotal.approvedStatePlan),
          formatAmountForExcel(sectorTotal.approvedNegotiatedLoan),
        ]);
      }
    });

  // Add grand total
  const grandTotal = Object.values(sectorTotals).reduce(
    (acc, sector) => ({
      approvedTotal: acc.approvedTotal + sector.approvedTotal,
      approvedStatePlan: acc.approvedStatePlan + sector.approvedStatePlan,
      approvedNegotiatedLoan:
        acc.approvedNegotiatedLoan + sector.approvedNegotiatedLoan,
    }),
    { approvedTotal: 0, approvedStatePlan: 0, approvedNegotiatedLoan: 0 },
  );

  excelData.push([
    "",
    "Grand Total",
    formatAmountForExcel(grandTotal.approvedTotal),
    formatAmountForExcel(grandTotal.approvedStatePlan),
    formatAmountForExcel(grandTotal.approvedNegotiatedLoan),
  ]);

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 8 }, // S.No.
    { wch: 40 }, // Department
    { wch: 25 }, // Proposed Total
    { wch: 20 }, // State Plan
    { wch: 20 }, // Negotiated Loan
  ];

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Session Budget");

  // Download
  downloadExcel(
    workbook,
    `Session_Budget_Department_Wise_${filters.financialYear || "All"}`,
  );
};

/**
 * Export Session Budget Descriptive (Project Wise)
 */
export const exportSessionBudgetDescriptiveToExcel = (
  data,
  filters,
  getDepartmentLabel,
) => {
  const { projects, totals, departmentTotals } = data;

  const excelData = [];

  // Add title row
  excelData.push([
    `Session Budget - Project Wise (${filters.financialYear || "All Years"})`,
  ]);
  if (filters.department) {
    excelData.push([`Department: ${getDepartmentLabel(filters.department)}`]);
  }
  excelData.push([]); // Empty row

  // Add headers
  excelData.push([
    "S.No.",
    "Department",
    "Project Name",
    "Source",
    "Project Status",
    "Amount (in lakh)",
    "State Plan/Negotiated Loan",
  ]);

  // If department filter is applied
  if (filters.department) {
    projects.forEach((project, index) => {
      excelData.push([
        index + 1,
        getDepartmentLabel(project.department),
        project.projectName || "",
        project.source || "",
        project.projectStatus === "new_project"
          ? "New Project"
          : "Ongoing Project",
        formatAmountForExcel(project.amount),
        project.clearanceType === "state_plan"
          ? "State Plan"
          : "Negotiated Loan",
      ]);
    });

    // Add total
    excelData.push([
      "",
      "Total",
      "",
      "",
      "",
      formatAmountForExcel(totals.totalFinancialRequirement),
      "",
    ]);
  } else {
    // Grouped by department
    const groupedByDept = {};
    projects.forEach((project) => {
      if (!groupedByDept[project.department]) {
        groupedByDept[project.department] = [];
      }
      groupedByDept[project.department].push(project);
    });

    let serialNumber = 1;

    Object.keys(groupedByDept).forEach((deptKey) => {
      const deptProjects = groupedByDept[deptKey];

      // Department header
      excelData.push([
        serialNumber++,
        getDepartmentLabel(deptKey),
        "",
        "",
        "",
        "",
        "",
      ]);

      // Projects
      deptProjects.forEach((project) => {
        excelData.push([
          "",
          getDepartmentLabel(project.department),
          project.projectName || "",
          project.source || "",
          project.projectStatus === "new_project"
            ? "New Project"
            : "Ongoing Project",
          formatAmountForExcel(project.amount),
          project.clearanceType === "state_plan"
            ? "State Plan"
            : "Negotiated Loan",
        ]);
      });

      // Department total
      const deptTotal = departmentTotals[deptKey] || 0;
      excelData.push([
        "",
        `Total - ${getDepartmentLabel(deptKey)}`,
        "",
        "",
        "",
        formatAmountForExcel(deptTotal),
        "",
      ]);
    });

    // Grand total
    excelData.push([
      "",
      "Grand Total",
      "",
      "",
      "",
      formatAmountForExcel(totals.totalFinancialRequirement),
      "",
    ]);
  }

  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  worksheet["!cols"] = [
    { wch: 8 }, // S.No.
    { wch: 40 }, // Department
    { wch: 35 }, // Project Name
    { wch: 20 }, // Source
    { wch: 18 }, // Project Status
    { wch: 18 }, // Amount
    { wch: 25 }, // Clearance Type
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Session Budget Projects");

  downloadExcel(
    workbook,
    `Session_Budget_Project_Wise_${filters.financialYear || "All"}`,
  );
};

/**
 * Export Departmental Allocation
 */
export const exportDepartmentalAllocationToExcel = (
  data,
  filters,
  getDepartmentLabel,
) => {
  const { departments } = data;

  const excelData = [];

  excelData.push([
    `State Share Allocation - Department Wise (${
      filters.financialYear || "All Years"
    })`,
  ]);
  excelData.push([]);

  excelData.push([
    "S.No.",
    "Department",
    "Cleared against allocation (in lakh)",
    "Cleared from Common Pool (in lakh)",
    "Additional State Share (in lakh)",
    "Total amount (in lakh)",
    "Approved allocation (in lakh)",
    "Balance against allocation (in lakh)",
  ]);

  departments.forEach((dept, index) => {
    excelData.push([
      index + 1,
      getDepartmentLabel(dept.department),
      formatAmountForExcel(dept.clearedAgainstAllocation),
      formatAmountForExcel(dept.clearedFromCommonPool),
      formatAmountForExcel(dept.additionalStateShare),
      formatAmountForExcel(dept.totalAmount),
      formatAmountForExcel(dept.approvedAllocation),
      formatAmountForExcel(dept.balanceAgainstAllocation),
    ]);
  });

  // Grand total
  const grandTotal = departments.reduce(
    (acc, dept) => ({
      clearedAgainstAllocation:
        acc.clearedAgainstAllocation + (dept.clearedAgainstAllocation || 0),
      clearedFromCommonPool:
        acc.clearedFromCommonPool + (dept.clearedFromCommonPool || 0),
      additionalStateShare:
        acc.additionalStateShare + (dept.additionalStateShare || 0),
      totalAmount: acc.totalAmount + (dept.totalAmount || 0),
      approvedAllocation:
        acc.approvedAllocation + (dept.approvedAllocation || 0),
      balanceAgainstAllocation:
        acc.balanceAgainstAllocation + (dept.balanceAgainstAllocation || 0),
    }),
    {
      clearedAgainstAllocation: 0,
      clearedFromCommonPool: 0,
      additionalStateShare: 0,
      totalAmount: 0,
      approvedAllocation: 0,
      balanceAgainstAllocation: 0,
    },
  );

  excelData.push([
    "",
    "Grand Total",
    formatAmountForExcel(grandTotal.clearedAgainstAllocation),
    formatAmountForExcel(grandTotal.clearedFromCommonPool),
    formatAmountForExcel(grandTotal.additionalStateShare),
    formatAmountForExcel(grandTotal.totalAmount),
    formatAmountForExcel(grandTotal.approvedAllocation),
    formatAmountForExcel(grandTotal.balanceAgainstAllocation),
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  worksheet["!cols"] = [
    { wch: 8 }, // S.No.
    { wch: 40 }, // Department
    { wch: 25 }, // Cleared against allocation
    { wch: 25 }, // Cleared from Common Pool
    { wch: 25 }, // Additional State Share
    { wch: 20 }, // Total amount
    { wch: 25 }, // Approved allocation
    { wch: 28 }, // Balance
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Departmental Allocation");

  downloadExcel(
    workbook,
    `Departmental_Allocation_${filters.financialYear || "All"}`,
  );
};

/**
 * Export Project Wise Allocation
 */
export const exportProjectWiseAllocationToExcel = (
  data,
  filters,
  getDepartmentLabel,
) => {
  const { allocations, departmentTotals, departmentApprovedAmounts } = data;

  const excelData = [];

  excelData.push([
    `State Share Allocation - Project Wise (${
      filters.financialYear || "All Years"
    })`,
  ]);
  excelData.push([]);

  excelData.push([
    "S.No.",
    "Department",
    "Project Name",
    "Cleared against allocation (in lakh)",
    "Cleared from Common Pool (in lakh)",
    "Additional State Share (in lakh)",
    "Total Amount (in lakh)",
    "Funding Pattern",
    "Central Share (in lakh)",
    "Approved Amount (in lakh)",
    "Balance State Share against allocation (in lakh)",
  ]);

  // Group by department
  const groupedByDept = {};
  allocations.forEach((allocation) => {
    const dept = allocation.department || "unknown";
    if (!groupedByDept[dept]) {
      groupedByDept[dept] = [];
    }
    groupedByDept[dept].push(allocation);
  });

  let serialNumber = 1;

  Object.keys(groupedByDept).forEach((deptKey) => {
    const deptAllocations = groupedByDept[deptKey];
    const isUnknown = !deptKey || deptKey === "unknown";

    // Department header
    excelData.push([
      "",
      isUnknown
        ? "New Projects (Please select departments)"
        : getDepartmentLabel(deptKey),
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);

    // Calculate department aggregates
    const deptAggregates = deptAllocations.reduce(
      (acc, allocation) => {
        const clearedAgainstAllocation =
          parseFloat(allocation.clearedAgainstAllocation) || 0;
        const clearedFromCommonPool =
          parseFloat(allocation.clearedFromCommonPool) || 0;
        const additionalStateShare =
          parseFloat(allocation.additionalStateShare) || 0;
        const totalAmount =
          clearedAgainstAllocation +
          clearedFromCommonPool +
          additionalStateShare;

        return {
          clearedAgainstAllocation:
            acc.clearedAgainstAllocation + clearedAgainstAllocation,
          clearedFromCommonPool:
            acc.clearedFromCommonPool + clearedFromCommonPool,
          additionalStateShare: acc.additionalStateShare + additionalStateShare,
          totalAmount: acc.totalAmount + totalAmount,
        };
      },
      {
        clearedAgainstAllocation: 0,
        clearedFromCommonPool: 0,
        additionalStateShare: 0,
        totalAmount: 0,
      },
    );

    const approvedAmount = departmentApprovedAmounts?.[deptKey] || 0;
    const balance = Math.max(
      0,
      approvedAmount - deptAggregates.clearedAgainstAllocation,
    );

    // Add department aggregate row
    excelData.push([
      "",
      "",
      "Department Total",
      formatAmountForExcel(deptAggregates.clearedAgainstAllocation),
      formatAmountForExcel(deptAggregates.clearedFromCommonPool),
      formatAmountForExcel(deptAggregates.additionalStateShare),
      formatAmountForExcel(deptAggregates.totalAmount),
      "",
      "",
      isUnknown ? "" : formatAmountForExcel(approvedAmount),
      isUnknown ? "" : formatAmountForExcel(balance),
    ]);

    // Projects
    deptAllocations.forEach((allocation) => {
      const clearedAgainstAllocation =
        parseFloat(allocation.clearedAgainstAllocation) || 0;
      const clearedFromCommonPool =
        parseFloat(allocation.clearedFromCommonPool) || 0;
      const additionalStateShare =
        parseFloat(allocation.additionalStateShare) || 0;
      const totalAmount =
        clearedAgainstAllocation + clearedFromCommonPool + additionalStateShare;

      excelData.push([
        serialNumber++,
        getDepartmentLabel(allocation.department),
        allocation.projectName || "",
        formatAmountForExcel(allocation.clearedAgainstAllocation),
        formatAmountForExcel(allocation.clearedFromCommonPool),
        formatAmountForExcel(allocation.additionalStateShare),
        formatAmountForExcel(totalAmount),
        allocation.fundingPattern || "",
        formatAmountForExcel(allocation.correspondingCentralShare),
        "—",
        "—",
      ]);
    });

    excelData.push([]); // Empty row between departments
  });

  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  worksheet["!cols"] = [
    { wch: 8 }, // S.No.
    { wch: 40 }, // Department
    { wch: 35 }, // Project Name
    { wch: 25 }, // Cleared against allocation
    { wch: 25 }, // Cleared from Common Pool
    { wch: 25 }, // Additional State Share
    { wch: 20 }, // Total Amount
    { wch: 18 }, // Funding Pattern
    { wch: 20 }, // Central Share
    { wch: 25 }, // Approved Amount
    { wch: 30 }, // Balance
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Project Allocations");

  downloadExcel(
    workbook,
    `Project_Wise_Allocation_${filters.financialYear || "All"}`,
  );
};

/**
 * Export Clearance Department Wise
 */
export const exportClearanceDepartmentWiseToExcel = (
  data,
  filters,
  getDepartmentLabel,
) => {
  const { departments, sectorTotals } = data;

  const excelData = [];

  excelData.push([
    `State Plan Clearance - Department Wise (${
      filters.financialYear || "All Years"
    })`,
  ]);
  excelData.push([]);

  excelData.push([
    "S.No.",
    "Department",
    "Total Approved Amount (in lakh)",
    "Total Cleared Amount (in lakh)",
    "State Plan Approved (in lakh)",
    "State Plan Cleared (in lakh)",
    "Negotiated Loan Approved (in lakh)",
    "Negotiated Loan Cleared (in lakh)",
  ]);

  let serialNumber = 1;

  // Group by sector
  const groupedBySector = {};
  departments.forEach((dept) => {
    if (!groupedBySector[dept.sector]) {
      groupedBySector[dept.sector] = [];
    }
    groupedBySector[dept.sector].push(dept);
  });

  Object.keys(groupedBySector)
    .sort()
    .forEach((sectorKey) => {
      const sectorDepts = groupedBySector[sectorKey];

      sectorDepts.forEach((dept) => {
        excelData.push([
          serialNumber++,
          getDepartmentLabel(dept.department),
          formatAmountForExcel(dept.approvedTotal),
          formatAmountForExcel(dept.clearanceTotal),
          formatAmountForExcel(dept.approvedStatePlan),
          formatAmountForExcel(dept.clearanceStatePlan),
          formatAmountForExcel(dept.approvedNegotiatedLoan),
          formatAmountForExcel(dept.clearanceNegotiatedLoan),
        ]);
      });
    });

  // Grand total
  const grandTotal = Object.values(sectorTotals).reduce(
    (acc, sector) => ({
      approvedTotal: acc.approvedTotal + sector.approvedTotal,
      clearanceTotal: acc.clearanceTotal + sector.clearanceTotal,
      approvedStatePlan: acc.approvedStatePlan + sector.approvedStatePlan,
      clearanceStatePlan: acc.clearanceStatePlan + sector.clearanceStatePlan,
      approvedNegotiatedLoan:
        acc.approvedNegotiatedLoan + sector.approvedNegotiatedLoan,
      clearanceNegotiatedLoan:
        acc.clearanceNegotiatedLoan + sector.clearanceNegotiatedLoan,
    }),
    {
      approvedTotal: 0,
      clearanceTotal: 0,
      approvedStatePlan: 0,
      clearanceStatePlan: 0,
      approvedNegotiatedLoan: 0,
      clearanceNegotiatedLoan: 0,
    },
  );

  excelData.push([
    "",
    "Grand Total",
    formatAmountForExcel(grandTotal.approvedTotal),
    formatAmountForExcel(grandTotal.clearanceTotal),
    formatAmountForExcel(grandTotal.approvedStatePlan),
    formatAmountForExcel(grandTotal.clearanceStatePlan),
    formatAmountForExcel(grandTotal.approvedNegotiatedLoan),
    formatAmountForExcel(grandTotal.clearanceNegotiatedLoan),
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  worksheet["!cols"] = [
    { wch: 8 }, // S.No.
    { wch: 40 }, // Department
    { wch: 25 }, // Total Approved
    { wch: 25 }, // Total Cleared
    { wch: 25 }, // State Plan Approved
    { wch: 25 }, // State Plan Cleared
    { wch: 28 }, // Negotiated Loan Approved
    { wch: 28 }, // Negotiated Loan Cleared
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Clearance Summary");

  downloadExcel(
    workbook,
    `Clearance_Department_Wise_${filters.financialYear || "All"}`,
  );
};

/**
 * Export Clearance Project Wise
 */
export const exportClearanceProjectWiseToExcel = (
  data,
  filters,
  getDepartmentLabel,
) => {
  const { projects } = data;

  const excelData = [];

  excelData.push([
    `State Plan Clearance - Project Wise (${
      filters.financialYear || "All Years"
    })`,
  ]);
  excelData.push([]);

  excelData.push([
    "S.No.",
    "Department",
    "Project Name",
    "Amount (in lakh)",
    "State Plan/Negotiated Loan",
    "Cleared (in lakh)",
    "UO No.",
    "Date",
  ]);

  // Group by department
  const groupedByDept = {};
  projects.forEach((project) => {
    if (!groupedByDept[project.department]) {
      groupedByDept[project.department] = [];
    }
    groupedByDept[project.department].push(project);
  });

  let serialNumber = 1;

  Object.keys(groupedByDept).forEach((deptKey) => {
    const deptProjects = groupedByDept[deptKey];

    deptProjects.forEach((project) => {
      const clearanceTypeLabel =
        project.clearanceType === "state_plan"
          ? "State Plan"
          : project.clearanceType === "negotiated_loan"
            ? "Negotiated Loan"
            : "—";

      const dateStr = project.clearanceDate
        ? new Date(project.clearanceDate).toLocaleDateString("en-IN")
        : "";

      excelData.push([
        serialNumber++,
        getDepartmentLabel(project.department),
        project.projectName || "",
        formatAmountForExcel(project.financialRequirement),
        clearanceTypeLabel,
        formatAmountForExcel(project.allocatedAmount),
        project.uoNumber || "",
        dateStr,
      ]);
    });
  });

  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  worksheet["!cols"] = [
    { wch: 8 }, // S.No.
    { wch: 40 }, // Department
    { wch: 35 }, // Project Name
    { wch: 18 }, // Amount
    { wch: 25 }, // Clearance Type
    { wch: 18 }, // Cleared
    { wch: 20 }, // UO No.
    { wch: 15 }, // Date
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Clearance Projects");

  downloadExcel(
    workbook,
    `Clearance_Project_Wise_${filters.financialYear || "All"}`,
  );
};

/**
 * Export Ongoing Projects
 */
export const exportOngoingProjectsToExcel = (
  data,
  filters,
  getDepartmentLabel,
) => {
  const { projects, grandTotals } = data;

  const excelData = [];

  // Add title row
  excelData.push([
    `Ongoing Projects Overview${
      filters.financialYear ? ` - ${filters.financialYear}` : ""
    }`,
  ]);
  excelData.push([]); // Empty row

  // Add headers
  excelData.push([
    "S. No.",
    "Department",
    "Project Name",
    "Year of Commencement",
    "Total Approved Cost (in lakh)",
    "Total Clearance (in lakh)",
    "Balance Remaining (in lakh)",
    "Remarks",
  ]);

  // Group by department
  const groupedByDept = {};
  projects.forEach((project) => {
    if (!groupedByDept[project.department]) {
      groupedByDept[project.department] = [];
    }
    groupedByDept[project.department].push(project);
  });

  let serialNumber = 1;

  // Sort department keys alphabetically
  const sortedDeptKeys = Object.keys(groupedByDept).sort((a, b) =>
    getDepartmentLabel(a).localeCompare(getDepartmentLabel(b)),
  );

  // Add data rows department by department
  sortedDeptKeys.forEach((deptKey) => {
    const deptProjects = groupedByDept[deptKey];

    // Add department projects
    deptProjects.forEach((project) => {
      excelData.push([
        serialNumber++,
        getDepartmentLabel(project.department),
        project.projectName || "",
        project.yearOfCommencement || "N/A",
        formatAmountForExcel(project.totalEstimatedCost),
        formatAmountForExcel(project.totalExpenditure),
        formatAmountForExcel(project.balanceRemaining),
        project.remarks || "—",
      ]);
    });

    // Add department total
    const deptTotals = {
      totalEstimatedCost: deptProjects.reduce(
        (sum, p) => sum + (parseFloat(p.totalEstimatedCost) || 0),
        0,
      ),
      totalExpenditure: deptProjects.reduce(
        (sum, p) => sum + (parseFloat(p.totalExpenditure) || 0),
        0,
      ),
      balanceRemaining: deptProjects.reduce(
        (sum, p) => sum + (parseFloat(p.balanceRemaining) || 0),
        0,
      ),
    };

    excelData.push([
      "",
      `Total - ${getDepartmentLabel(deptKey)} (${
        deptProjects.length
      } projects)`,
      "",
      "",
      formatAmountForExcel(deptTotals.totalEstimatedCost),
      formatAmountForExcel(deptTotals.totalExpenditure),
      formatAmountForExcel(deptTotals.balanceRemaining),
      "",
    ]);
  });

  // Add grand total
  excelData.push([
    "",
    `Grand Total (${grandTotals.projectCount} projects)${
      filters.search || filters.department || filters.yearOfCommencement
        ? " (Filtered)"
        : ""
    }`,
    "",
    "",
    formatAmountForExcel(grandTotals.totalEstimatedCost),
    formatAmountForExcel(grandTotals.totalExpenditure),
    formatAmountForExcel(grandTotals.balanceRemaining),
    "",
  ]);

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 8 }, // S. No.
    { wch: 40 }, // Department
    { wch: 45 }, // Project Name
    { wch: 20 }, // Year of Commencement
    { wch: 25 }, // Total Approved Cost
    { wch: 25 }, // Total Clearance
    { wch: 25 }, // Balance Remaining
    { wch: 30 }, // Remarks
  ];

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Ongoing Projects");

  // Download
  const fileName = filters.financialYear
    ? `Ongoing_Projects_${filters.financialYear}`
    : "Ongoing_Projects_All";
  downloadExcel(workbook, fileName);
};

/**
 * Export Project Approval
 */
export const exportProjectApprovalToExcel = (
  data,
  filters,
  getDepartmentLabel,
  getSectorLabel,
) => {
  const { projects } = data;

  const excelData = [];

  // Add title row
  excelData.push([
    `Project Approval${
      filters.financialYear ? ` - ${filters.financialYear}` : ""
    }`,
  ]);
  excelData.push([]); // Empty row

  // Add headers
  excelData.push([
    "S. No.",
    "Department",
    "Sector",
    "Project Name",
    "Financial Year",
    "Proposed Total Cost (in lakh)",
    "Approved Total Cost (in lakh)",
    "Dept. Priority",
    "Status",
    "Submitted Date",
  ]);

  // Add data rows
  projects.forEach((project, index) => {
    const statusLabel =
      project.status === "pending"
        ? "Pending"
        : project.status === "approved"
          ? "Approved"
          : project.status === "rejected"
            ? "Rejected"
            : project.status;

    const submittedDate = project.createdAt
      ? new Date(project.createdAt).toLocaleDateString("en-IN")
      : "";

    excelData.push([
      index + 1,
      getDepartmentLabel(project.department),
      getSectorLabel(project.sector),
      project.projectName || "",
      project.financialYear ? `FY ${project.financialYear}` : "N/A",
      formatAmountForExcel(project.financialRequirement),
      project.status === "approved" && project.approvedAmount
        ? formatAmountForExcel(project.approvedAmount)
        : "—",
      project.priority || "—",
      statusLabel,
      submittedDate,
    ]);
  });

  // Add summary
  excelData.push([]); // Empty row
  excelData.push(["Summary:"]);
  excelData.push(["Total Projects:", projects.length]);
  excelData.push([
    "Pending:",
    projects.filter((p) => p.status === "pending").length,
  ]);
  excelData.push([
    "Approved:",
    projects.filter((p) => p.status === "approved").length,
  ]);
  excelData.push([
    "Rejected:",
    projects.filter((p) => p.status === "rejected").length,
  ]);

  const totalProposed = projects.reduce(
    (sum, p) => sum + (parseFloat(p.financialRequirement) || 0),
    0,
  );
  const totalApproved = projects
    .filter((p) => p.status === "approved")
    .reduce((sum, p) => sum + (parseFloat(p.approvedAmount) || 0), 0);

  excelData.push([
    "Total Proposed Amount (in lakh):",
    formatAmountForExcel(totalProposed),
  ]);
  excelData.push([
    "Total Approved Amount (in lakh):",
    formatAmountForExcel(totalApproved),
  ]);

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 8 }, // S. No.
    { wch: 40 }, // Department
    { wch: 25 }, // Sector
    { wch: 45 }, // Project Name
    { wch: 18 }, // Financial Year
    { wch: 25 }, // Proposed Total Cost
    { wch: 25 }, // Approved Total Cost
    { wch: 15 }, // Dept. Priority
    { wch: 15 }, // Status
    { wch: 18 }, // Submitted Date
  ];

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Project Approval");

  // Download
  const fileName = filters.financialYear
    ? `Project_Approval_${filters.financialYear}`
    : "Project_Approval_All";
  downloadExcel(workbook, fileName);
};
